/**
 * Steps cloned from an outreach template (mb-frontend#4638, "phase 2").
 *
 * A phase-2 step is an `email_campaign` / `heyreach_campaign` play whose custom_action_details
 * carries `step_type` (profile_view, connection_request, new_thread, ...) and `mode` (generated,
 * ai, none, skip). Its copy is decided per person at send time or comes from the template, so it
 * has no saved email template and no `steps[]`. The rules here mirror audience-processor's
 * src/utils/phase2TemplateSteps.js, so the builder reads a step the same way the worker runs it.
 */

type StepDetails = Record<string, unknown> | null | undefined;

/** Whether custom_action_details came from a phase-2 template step (`step_type` and `mode`). */
export const isPhase2TemplateStep = (details: StepDetails): boolean => (
  !!details
  && typeof details.step_type === 'string' && details.step_type.length > 0
  && typeof details.mode === 'string' && details.mode.length > 0
);

/**
 * Whether a play is a phase-2 LinkedIn step. Adding `steps[]` to one turns it into a legacy
 * composite play in the worker, whose acceptance gate passes on only the people who accepted,
 * so everyone who did not accept stops getting the template's later emails.
 */
export const isPhase2LinkedInStep = (actionType: unknown, details: StepDetails): boolean => (
  actionType === 'heyreach_campaign'
  && isPhase2TemplateStep(details)
  && !Array.isArray(details?.steps)
);

/** The template's own fixed copy: mode 'generated' with a subject or body to render. */
export const isPhase2FixedCopy = (details: StepDetails): boolean => (
  !!details && details.mode === 'generated'
  && (typeof details.subject === 'string' || typeof details.body === 'string')
);

const LINKEDIN_STEP_LABEL: Record<string, string> = {
  profile_view: 'View profile',
  connection_request: 'Connection request',
  message_after_connection_accepted: 'Message after accept',
  if_not_accepted: 'If not accepted',
};

/** A phase-2 LinkedIn step's label, from its step_type. */
export const phase2LinkedInStepLabel = (details: StepDetails): string => (
  LINKEDIN_STEP_LABEL[String(details?.step_type)] ?? 'Template step'
);

/**
 * A phase-2 email step's copy label, or null when its mode says nothing the canvas can show.
 * Mode 'none' sends no email at all (the worker records the step and moves people on).
 */
export const phase2EmailCopyLabel = (details: StepDetails): string | null => {
  if (!isPhase2TemplateStep(details)) return null;
  if (details?.mode === 'ai') return 'AI-written';
  if (isPhase2FixedCopy(details)) return 'Template copy';
  if (details?.mode === 'none') return 'No email';
  return null;
};
